import React from 'react';

import cn from 'classnames';
import PropTypes from 'prop-types';
import { GoPlus } from 'react-icons/go';
import { CgSpinner, CgArrowsExpandLeft } from 'react-icons/cg';
import { MdOutlineDragIndicator, MdImageNotSupported } from 'react-icons/md';
import { IoMdImage } from 'react-icons/io';
import { IoClose } from 'react-icons/io5';
import { sortableHandle } from 'react-sortable-hoc';

import {
  Checkbox,
  showLightBox,
  PhotoGallery,
  uploadPhoto,
  toastError,
} from 'modules/common';

const DragHandle = sortableHandle(() => (
  <MdOutlineDragIndicator
    className="text-white h-6 w-6"
    style={{
      cursor: 'grab',
    }}
  />
));

const OverlayWrapper = ({ className, children, isDark, ...rest }) => (
  <div
    className={cn(
      'absolute top-0 left-0 w-full h-full flex rounded z-[1]',
      isDark
        ? 'bg-primitive-black bg-opacity-60'
        : 'bg-[#f9fafb] hover:bg-dark',
      className
    )}
    {...rest}
    role="presentation"
  >
    {children}
  </div>
);

OverlayWrapper.defaultProps = {
  children: false,
  isDark: false,
  className: '',
};

OverlayWrapper.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.instanceOf(Object),
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  isDark: PropTypes.bool,
  className: PropTypes.string,
};

function Image({
  src: $src,
  containerClassName,
  className,
  size,
  withAdd,
  withAddPhotoGallery,
  withRemove,
  withCheckbox,
  withResize,
  withDrag,
  selected,
  height,
  width,
  onRemove,
  defaultImageClassName,
  photoGalleryProps,
  fileName,
  fileSize,
  isLoading: $isLoading,
  setIsLoading: $setIsLoading,
  onChange,
  onSetFieldValue,
  name,
  addIconClassName,
  spinnerIconClassName,
  // Upload Care
  withImageOptimize,
}) {
  const [isImageLoading, setIsImageLoading] = React.useState(true);
  const [isError, setIsError] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState($isLoading);

  const src = $src || '';
  const isImageBroken = src?.length > 0 && isError && !isImageLoading;
  const isDefaultImage = !withAdd && !withAddPhotoGallery && src?.length <= 0;
  const $withAdd = (withAdd || withAddPhotoGallery) && src?.length <= 0;
  const isLoaderSpinning = src?.length > 0 && isImageLoading;
  const isImageSuccessful = src?.length > 0 && !isError && !isImageLoading;

  const renderBorder = () => {
    if (isImageSuccessful) {
      return 'border-transparent';
    }

    if (!withAdd && !withAddPhotoGallery) {
      return 'border-default';
    }

    return 'border-default border-dashed';
  };

  const dispatchSrc = () => {
    if (src) {
      if (withImageOptimize) {
        return `${src}-/format/jpeg/-/progressive/yes/-/preview/400x400/`;
      }

      return src;
    }

    return '';
  };

  const handleOnClick = () => {
    if (isImageSuccessful) {
      showLightBox({
        content: (onClose) => (
          <PhotoGallery
            options={[
              {
                fileName,
                fileSize,
                url: dispatchSrc(),
              },
            ]}
            selectedIndex={0}
            withBar={false}
            barProps={{
              withViewGallery: false,
            }}
            isSolo
            onClose={onClose}
            {...photoGalleryProps}
          />
        ),
      });
    }
  };

  const handleOnLoad = () => {
    setTimeout(() => {
      setIsImageLoading(false);
      setIsError(false);
    }, 50);
  };

  const handleOnError = () => {
    setTimeout(() => {
      setIsImageLoading(false);
      setIsError(true);
    }, 50);
  };

  const handleOnChange = (v) => {
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, v);
      return;
    }

    if (typeof onChange === 'function') {
      onChange((prev) => ({
        ...prev,
        [name]: v,
      }));
    }
  };

  const handleOnSelect = (v, onClose) => {
    handleOnChange(v?.options?.[v?.selectedIndex]?.url);
    onClose();
  };

  const handleOnAdd = async () => {
    if (withAddPhotoGallery) {
      showLightBox({
        content: (onClose) => (
          <PhotoGallery
            options={[]}
            withBar
            barProps={{
              withViewGallery: false,
            }}
            onClose={onClose}
            {...photoGalleryProps}
            onSelect={(v) => handleOnSelect(v, onClose)}
          />
        ),
      });
      return;
    }

    try {
      setIsLoading(true);
      $setIsLoading(true);
      const $v = await uploadPhoto({
        onCatch: () => {
          setIsLoading(false);
          $setIsLoading(false);
        },
      });
      setIsLoading(false);
      $setIsLoading(false);

      handleOnChange($v);
    } catch (err) {
      toastError('Unable to upload file');
    }
  };

  React.useEffect(() => {
    setIsImageLoading(true);
    setIsError(false);
  }, [src]);

  return (
    <div
      className={cn(
        'border-2 cursor-pointer rounded bg-[#f9fafb] hover:bg-light relative group',
        containerClassName,
        renderBorder(),
        {
          '!border-brand-primary-base !rounded-md !border-solid': selected,
        }
      )}
      style={{
        height: height !== false ? height : size,
        width: width !== false ? width : size,
      }}
      onClick={handleOnClick}
      role="presentation"
    >
      {/* spinner - is src loading */}
      {isLoaderSpinning && (
        <OverlayWrapper>
          <CgSpinner
            className={cn(
              'h-7 w-7 lg:h-9 lg:w-9 m-auto animate-spin text-muted',
              spinnerIconClassName
            )}
          />
        </OverlayWrapper>
      )}

      {/* plus - has no src */}
      {$withAdd && (
        <OverlayWrapper onClick={handleOnAdd}>
          <GoPlus
            className={cn(
              'h-[18px] w-[18px] lg:h-6 lg:w-6 m-auto text-muted',
              addIconClassName
            )}
          />
        </OverlayWrapper>
      )}

      {/* default image */}
      {isDefaultImage && (
        <OverlayWrapper>
          <IoMdImage
            className={cn(
              'h-10 w-10 m-auto rounded-md text-muted',
              defaultImageClassName
            )}
          />
        </OverlayWrapper>
      )}

      {/* image error */}
      {isImageBroken && (
        <OverlayWrapper>
          <MdImageNotSupported
            className={cn(
              'h-10 w-10 m-auto rounded-md text-placeholder',
              defaultImageClassName
            )}
          />
        </OverlayWrapper>
      )}

      {/* full screen & close */}
      {isImageSuccessful && (
        <>
          <OverlayWrapper isDark className="hidden group-hover:flex">
            {withResize && (
              <CgArrowsExpandLeft className="h-4 w-4 m-auto text-white" />
            )}
          </OverlayWrapper>

          {withRemove && (
            <div className="hidden group-hover:block">
              <button
                className="absolute -top-1.5 -right-1.5 z-[2] h-4 w-4 sm:h-6 sm:w-6 border border-default text-muted bg-light hover:text-primitive-white hover:border-transparent hover:bg-utility-danger-base flex rounded-full transition-all duration-150 ease-in"
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
              >
                <IoClose className="h-3 w-3 sm:h-3.5 sm:w-3.5 m-auto" />
              </button>
            </div>
          )}
        </>
      )}

      {(isImageSuccessful || isDefaultImage || isImageBroken) && (
        <>
          {withCheckbox && !withDrag && (
            <div className="absolute left-1.5 top-1.5 z-[1]">
              <Checkbox value={selected} />
            </div>
          )}

          {withDrag && (
            <div className="absolute left-[3px] top-1.5 z-[1] hidden group-hover:block">
              <DragHandle />
            </div>
          )}
        </>
      )}

      {/* isImageLoading props loading */}
      {isLoading && (
        <OverlayWrapper>
          <CgSpinner
            className={cn(
              'h-7 w-7 lg:h-9 lg:w-9 m-auto animate-spin text-muted',
              spinnerIconClassName
            )}
          />
        </OverlayWrapper>
      )}

      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      <img
        src={dispatchSrc()}
        onLoad={handleOnLoad}
        onError={handleOnError}
        className={cn('object-cover h-full w-full rounded', className, {
          'opacity-0': !isImageSuccessful,
        })}
      />
    </div>
  );
}

Image.defaultProps = {
  size: '100px',
  withAdd: false,
  src: '',
  className: '',
  containerClassName: '',
  withRemove: true,
  withCheckbox: false,
  withResize: true,
  selected: false,
  withDrag: false,
  height: false,
  width: false,
  onRemove: () => {},
  setIsLoading: () => {},
  defaultImageClassName: '',
  photoGalleryProps: {},
  fileName: '',
  fileSize: '',
  name: '',
  isLoading: false,
  onChange: false,
  onSetFieldValue: false,
  withImageOptimize: false,
  withAddPhotoGallery: false,
  addIconClassName: '',
  spinnerIconClassName: '',
};

Image.propTypes = {
  src: PropTypes.string,
  size: PropTypes.string,
  fileName: PropTypes.string,
  fileSize: PropTypes.string,
  name: PropTypes.string,
  withImageOptimize: PropTypes.bool,
  withAddPhotoGallery: PropTypes.bool,
  defaultImageClassName: PropTypes.string,
  addIconClassName: PropTypes.string,
  spinnerIconClassName: PropTypes.string,
  className: PropTypes.string,
  height: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  width: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.bool,
  ]),
  selected: PropTypes.bool,
  withDrag: PropTypes.bool,
  containerClassName: PropTypes.string,
  withAdd: PropTypes.bool,
  withRemove: PropTypes.bool,
  withResize: PropTypes.bool,
  onChange: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.instanceOf(Function),
  ]),
  photoGalleryProps: PropTypes.instanceOf(Object),
  onRemove: PropTypes.instanceOf(Function),
  setIsLoading: PropTypes.instanceOf(Function),
  withCheckbox: PropTypes.bool,
  isLoading: PropTypes.bool,
};

export default Image;
